import React, { type ReactNode } from 'react';
import Button from '@atlaskit/button/standard-button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalTransition,
	ModalBody,
	ModalTitle,
	ModalHeader,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { toProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { MSTEAM_APP_TYPE, SLACK_APP_TYPE, type AppType } from '../../../../../../common/constants';
import {
	useSlackWorkspaceConnections,
	useMsTeamsConnectionStatusInfo,
} from '../../../../../../services/main.tsx';
import { getRemoveModalContent, getRemoveModalHeading, messages } from './messages';

type Props = {
	connectionId: string;
	workspaceName: string;
	onClose: () => void;
	appType: AppType;
};

export const RemoveWorkspaceModal = ({ onClose, connectionId, appType, workspaceName }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useTenantContext();
	const { data: projectContext } = useProjectContext();
	const [, { removeSlackWorkspaceConnectionsForProject }] = useSlackWorkspaceConnections();

	const [, { setMsTeamsWorkspaceConnectionStateForProject }] = useMsTeamsConnectionStatusInfo();

	const projectId = projectContext ? toProjectId(projectContext.projectId.toString()) : undefined;

	const onDeleteWorkspaceConnection = () => {
		const event = createAnalyticsEvent({});
		switch (appType) {
			case SLACK_APP_TYPE:
				removeSlackWorkspaceConnectionsForProject({
					cloudId,
					projectId,
					connectionId,
					analyticsEvent: event,
				});
				onClose();
				break;
			case MSTEAM_APP_TYPE:
				setMsTeamsWorkspaceConnectionStateForProject({
					connectionId,
					projectId: projectId ?? '',
					enabled: false,
				});
				onClose();
				break;
			default:
				break;
		}
	};

	const { formatMessage } = useIntl();

	return (
		<ModalTransition>
			<ShortcutScope>
				<ModalDialog onClose={onClose}>
					<ModalHeader>
						<ModalTitle appearance="warning">
							{formatMessage(getRemoveModalHeading(appType))}
						</ModalTitle>
					</ModalHeader>

					<ModalBody>
						{ff('agni-replace-typography-css-with-tokens_cgl2g') ? (
							<Text as="p">
								{formatMessage(getRemoveModalContent(appType), {
									workspaceName,
									b: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
								})}
							</Text>
						) : (
							<p>
								{formatMessage(getRemoveModalContent(appType), {
									workspaceName,
									// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
									b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
								})}
							</p>
						)}
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={onClose}
							appearance="subtle"
							testId="servicedesk-incident-management-settings.ui.communication-bridges.slack-workplace-connection-settings.workspaces.workspace.remove-slack-workspace-modal.cancel-button"
						>
							{formatMessage(messages.workspaceConnectionRemoveModalCancelButton)}
						</Button>
						<Button
							onClick={onDeleteWorkspaceConnection}
							autoFocus
							appearance="warning"
							testId="servicedesk-incident-management-settings.ui.communication-bridges.slack-workplace-connection-settings.workspaces.workspace.remove-slack-workspace-modal.remove-button"
						>
							{formatMessage(messages.workspaceConnectionRemoveModalRemoveButton)}
						</Button>
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		</ModalTransition>
	);
};
